export default {
  "slug": "clearing-fire-alarm-ground-faults",
  "title": "Ground Faults on Fire Alarm Circuits: How to Find and Clear Them",
  "description": "Why fire alarm ground faults happen, how the panel detects leakage to building ground, and a step-by-step method to isolate and clear an intermittent ground on initiating, notification, and SLC circuits.",
  "keywords": "how to clear a fire alarm ground fault, fire alarm ground fault troubleshooting, earth fault, intermittent ground, SLC ground, NFPA 72 ground fault detection",
  "faqs": [
    {
      "q": "Why does my fire alarm keep showing a ground fault?",
      "a": "A ground fault means a circuit conductor is leaking current to building ground. Recurring or intermittent grounds are usually caused by moisture, water intrusion, or a nicked conductor touching a grounded enclosure or conduit. Because the condition can come and go with humidity, the panel may report it only some of the time."
    },
    {
      "q": "Is a ground fault an emergency?",
      "a": "A ground fault is annunciated as a trouble, not an alarm, so the building is not being evacuated. But it is not harmless: a second fault on the same circuit could disable devices. NFPA 72 requires troubles to be corrected promptly so the system stays ready to signal a real fire."
    },
    {
      "q": "How do I find which circuit has the ground?",
      "a": "Isolate circuits one at a time at the panel. When you lift the conductors of the faulted circuit and the ground trouble clears, you've found the loop. Then halve that loop to narrow to a segment, device, or splice, measuring resistance from each conductor to building ground as you go."
    }
  ],
  "category": "Troubleshooting",
  "readMins": 6,
  "published": "2026-09-27",
  "updated": "2026-09-27",
  "hero": "https://images.unsplash.com/photo-1544724569-5f546fd6f2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxmaXJlJTIwYWxhcm0lMjBjb250cm9sJTIwcGFuZWwlMjB3aXJpbmd8ZW58MHx8fHwxNzkwNTI2MDE0fDA&ixlib=rb-4.1.0&q=85",
  "heroAlt": "Fire alarm control panel with colored field wiring on terminal blocks",
  "excerpt": "Ground faults are the most common — and most maddening — fire alarm trouble. Here's a calm, methodical way to find and clear one.",
  "sections": [
    {
      "h": "What a ground fault actually is",
      "p": "Fire alarm circuits are designed to float relative to building ground so the panel can supervise them. A ground fault is unintended leakage from one of those conductors to earth — the metal box, the conduit, or building steel. The panel's ground-fault detection senses this leakage and annunciates a trouble. Because leakage often depends on moisture, grounds are famous for appearing overnight and vanishing by mid-morning."
    },
    {
      "h": "Common root causes",
      "p": "Most grounds trace back to a handful of causes: water intrusion at exterior or rooftop devices, condensation in cold boxes, a conductor nicked during stripping that now touches a grounded enclosure, staples or clamps over-driven into cable, or a device with internal moisture. Areas washed down, parking garages, and rooftop duct detectors are repeat offenders."
    },
    {
      "h": "Isolate the faulted circuit",
      "p": "With the ground trouble present, lift circuits one at a time at the panel terminals. When the trouble clears as you disconnect a particular circuit, that loop contains the ground. Work safely, follow the manufacturer's power-down guidance, and note that lifting a circuit takes those devices out of service — coordinate a fire watch or impairment procedure per the facility's policy while you work."
    },
    {
      "h": "Halve the loop to the segment",
      "p": "On the faulted loop, open the pathway at a midpoint junction and see which half still grounds. Repeat until you reach a single run, device, or splice. A meter reading resistance from each conductor to a known building ground guides you — a clean segment reads very high (essentially open to ground), while the faulted segment reads low."
    },
    {
      "h": "Clear, dry, and prevent the repeat",
      "p": "Once located, repair the insulation, re-terminate cleanly, or replace the affected device. If moisture was the cause, dry and seal the enclosure and address the water path — otherwise the ground returns with the next rain. Restore all isolations, confirm the panel returns to normal with full supervision, re-test the devices, and document the root cause so the next technician isn't chasing the same intermittent fault."
    }
  ],
  "relatedModules": [
    "troubleshooting",
    "wiring-circuitry",
    "signal-monitoring"
  ],
  "relatedGuides": [
    "commercial-fire-alarm-troubleshooting",
    "fire-alarm-wiring-and-circuits"
  ]
};
